import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { tenantApi } from '../../api/endpoints';
import type { MyInvitation } from '../../api/types';
import { useI18n } from '../../i18n';
import { errorMessage, Icon, useToast } from '../../ui';
import { rememberCompany, startPath } from './hooks';

// Invitations addressed to me follow me everywhere (TF-78): the start page lists them, the
// dashboard shows a banner, the company switcher a badge. Accepting switches to the new company.

/** My invitations, and - when my e-mail is not verified - the address that needs verifying. */
export function useMyInvitations() {
  return useQuery({ queryKey: ['my-invitations'], queryFn: tenantApi.myInvitations, staleTime: 60_000 });
}

export function useInvitationActions() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const accept = useMutation({
    mutationFn: (id: string) => tenantApi.acceptInvitation(id),
    onSuccess: async ({ tenant, role }) => {
      await queryClient.invalidateQueries({ queryKey: ['companies'] });
      await queryClient.invalidateQueries({ queryKey: ['my-invitations'] });
      rememberCompany(tenant.id);
      toast.info(t('invitations.accepted', { name: tenant.name }));
      // A member to the company's dashboard; a guest to what is shared with them.
      navigate(startPath({ id: tenant.id, role }));
    },
    onError: (error) => toast.error(errorMessage(error, t('common.somethingWrong'))),
  });
  const decline = useMutation({
    mutationFn: (invitation: MyInvitation) => tenantApi.declineInvitation(invitation.id),
    onSuccess: async (_nothing, invitation) => {
      await queryClient.invalidateQueries({ queryKey: ['my-invitations'] });
      toast.info(t('invitations.declined', { name: invitation.tenantName }));
    },
    onError: (error) => toast.error(errorMessage(error, t('common.somethingWrong'))),
  });
  return { accept, decline };
}

const LATER_KEY = 'teamfolio.invitationsLater';
const laterIds = (): string[] => {
  try {
    return JSON.parse(sessionStorage.getItem(LATER_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
};

/**
 * At the top of the dashboard: each invitation for me, with Accept, Decline and Later. Later hides
 * it until the next visit (this browser session), so it comes back until answered.
 */
export function InvitationBanner() {
  const { t } = useI18n();
  const invitations = useMyInvitations();
  const { accept, decline } = useInvitationActions();
  const [later, setLater] = useState<string[]>(laterIds);
  const shown = (invitations.data?.invitations ?? []).filter((invitation) => !later.includes(invitation.id));
  if (shown.length === 0) return null;
  const postpone = (id: string) => {
    const next = [...later, id];
    setLater(next);
    try {
      sessionStorage.setItem(LATER_KEY, JSON.stringify(next));
    } catch {
      // Hidden until the page is left.
    }
  };
  return (
    <div className="invitation-banners">
      {shown.map((invitation) => (
        <section key={invitation.id} className="banner invitation-banner" aria-label={t('invitations.title')}>
          <Icon name="mail" className="icon-lead" />
          <p className="grow m-0">{t('invitations.banner', { name: invitation.tenantName, role: t(`role.${invitation.role}`).toLowerCase() })}</p>
          <div className="btn-row">
            <button type="button" className="btn btn-sm btn-tonal" disabled={accept.isPending} onClick={() => accept.mutate(invitation.id)}>
              {t('invitations.accept')}
            </button>
            <button type="button" className="btn btn-sm" disabled={decline.isPending} onClick={() => decline.mutate(invitation)}>
              {t('invitations.decline')}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => postpone(invitation.id)}>
              {t('invitations.later')}
            </button>
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * Invitations are matched by a verified e-mail. When mine is not verified, the page says so, names
 * the address, and offers to send the verification e-mail (the likely reason an invited person
 * who just signed up sees no invitation).
 */
export function UnverifiedEmailNote({ email }: { email: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const send = useMutation({
    mutationFn: tenantApi.sendVerificationEmail,
    onSuccess: (result) => toast.info(result.sentTo ? t('invitations.verificationSent', { email: result.sentTo }) : t('invitations.verifiedNow')),
    onError: (error) => toast.error(errorMessage(error, t('common.somethingWrong'))),
  });
  return (
    <section className="card unverified-note" aria-labelledby="unverified-email">
      <h2 id="unverified-email">
        <Icon name="warning" className="icon-lead" />
        {t('invitations.unverifiedTitle')}
      </h2>
      <p>{t('invitations.unverified', { email })}</p>
      <button type="button" className="btn" disabled={send.isPending || send.isSuccess} onClick={() => send.mutate()}>
        <Icon name="mail" size={18} />
        {t('invitations.sendVerification')}
      </button>
    </section>
  );
}
